<template>
  <div class="archive-view">
    <!-- Search bar -->
    <div class="archive-toolbar">
      <div class="archive-search-wrap">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" class="archive-search-icon">
          <circle cx="6.5" cy="6.5" r="4.5"/><path d="M11 11l3 3"/>
        </svg>
        <input
          v-model="search"
          class="archive-search"
          placeholder="Search archived tasks…"
        />
        <button v-if="search" class="archive-search-clear" @click="search = ''">
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M2 2l8 8M10 2l-8 8"/>
          </svg>
        </button>
      </div>
      <span class="archive-count">{{ filteredTasks.length }} task{{ filteredTasks.length !== 1 ? 's' : '' }}</span>
    </div>

    <!-- Empty state -->
    <div v-if="archivedTasks.length === 0" class="archive-empty">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="width:36px;height:36px;opacity:0.25;">
        <path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4"/>
      </svg>
      <span>No archived tasks</span>
    </div>
    <div v-else-if="filteredTasks.length === 0" class="archive-empty">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="width:36px;height:36px;opacity:0.25;">
        <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
      </svg>
      <span>No tasks match "{{ search }}"</span>
    </div>

    <!-- Task list -->
    <div v-else class="archive-list">
      <div
        v-for="task in filteredTasks"
        :key="task.id"
        class="archive-item"
        :class="{ 'archive-item--loading': loadingId === task.id }"
      >
        <div class="archive-item__check">
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 6l3 3 5-5"/>
          </svg>
        </div>
        <div class="archive-item__body">
          <span class="archive-item__title">{{ taskTitle(task) }}</span>
          <div class="archive-item__meta">
            <span class="archive-item__project">{{ projectName(task.projectId) }}</span>
            <span v-if="task.deadline" class="archive-item__deadline">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="width:10px;height:10px;flex-shrink:0;">
                <rect x="2" y="3" width="12" height="11" rx="1.5"/><path d="M5 1v2M11 1v2M2 7h12"/>
              </svg>
              {{ fmtDate(task.deadline) }}
            </span>
            <span class="archive-item__archived-date">
              Archived {{ fmtDate(task.archivedAt) }}
            </span>
          </div>
        </div>
        <div class="archive-item__actions">
          <button
            class="archive-action archive-action--restore"
            :disabled="loadingId === task.id"
            @click="unarchive(task)"
            title="Restore task"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2 9a6 6 0 1011.93-1M2 9V5m0 4H6"/>
            </svg>
            Restore
          </button>
          <button
            class="archive-action archive-action--delete"
            :disabled="loadingId === task.id"
            @click="confirmDelete(task)"
            title="Delete permanently"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2 4h12M6 4V2.5h4V4M13 4l-.8 9.5H3.8L3 4"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Confirm delete dialog -->
    <div v-if="deleteTarget" class="archive-confirm-overlay" @click.self="deleteTarget = null">
      <div class="archive-confirm">
        <p class="archive-confirm__text">Delete "<strong>{{ taskTitle(deleteTarget) }}</strong>" permanently?</p>
        <div class="archive-confirm__actions">
          <button class="archive-confirm__btn archive-confirm__btn--cancel" @click="deleteTarget = null">Cancel</button>
          <button class="archive-confirm__btn archive-confirm__btn--delete" @click="deleteTask">Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import { getFirstLineFromHtml } from '@/utils'

  export default {
    name: 'ArchivedTasks',
    props: ['projectId'],
    data: () => ({
      search: '',
      loadingId: null,
      deleteTarget: null,
    }),
    computed: {
      archivedTasks() {
        return this.$store.getters.archivedTasks(this.projectId)
          .slice()
          .sort((a, b) => moment(b.archivedAt).diff(moment(a.archivedAt)))
      },
      filteredTasks() {
        if (!this.search.trim()) return this.archivedTasks
        const q = this.search.toLowerCase()
        return this.archivedTasks.filter(t => {
          const title = (getFirstLineFromHtml(t.description) || '').toLowerCase()
          const proj = this.projectName(t.projectId).toLowerCase()
          return title.includes(q) || proj.includes(q)
        })
      },
      projects() {
        return this.$store.getters.projects
      }
    },
    methods: {
      taskTitle(task) {
        return getFirstLineFromHtml(task.description) || 'Untitled'
      },
      projectName(projectId) {
        const proj = this.projects.find(p => p.id === projectId)
        return proj ? proj.title : ''
      },
      fmtDate(date) {
        if (!date) return ''
        const m = moment(date)
        return m.isSame(moment(), 'year') ? m.format('MMM D') : m.format('MMM D, YYYY')
      },
      async unarchive(task) {
        this.loadingId = task.id
        await this.$store.dispatch('updateTask', { id: task.id, archivedAt: null })
        this.loadingId = null
      },
      confirmDelete(task) {
        this.deleteTarget = task
      },
      async deleteTask() {
        if (!this.deleteTarget) return
        this.loadingId = this.deleteTarget.id
        this.deleteTarget = null
        await this.$store.dispatch('deleteTask', this.loadingId)
        this.loadingId = null
      },
    }
  }
</script>

<style scoped>
.archive-view {
  position: relative;
}

.archive-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.archive-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 360px;
}

.archive-search-icon {
  position: absolute;
  left: 10px;
  width: 14px;
  height: 14px;
  color: rgba(0,0,0,0.3);
  pointer-events: none;
}

.archive-search {
  width: 100%;
  padding: 7px 32px 7px 32px;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 8px;
  background: rgba(0,0,0,0.025);
  font-size: 0.875rem;
  color: rgba(0,0,0,0.8);
  outline: none;
  transition: border-color 0.12s;
}

.archive-search:focus {
  border-color: rgba(61,106,144,0.4);
  background: white;
}

.archive-search::placeholder { color: rgba(0,0,0,0.35); }

.archive-search-clear {
  position: absolute;
  right: 8px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: rgba(0,0,0,0.3);
  padding: 0;
  border-radius: 50%;
}
.archive-search-clear svg { width: 9px; height: 9px; }
.archive-search-clear:hover { color: rgba(0,0,0,0.6); background: rgba(0,0,0,0.06); }

.archive-count {
  font-size: 0.78rem;
  color: rgba(0,0,0,0.4);
  white-space: nowrap;
}

/* List */
.archive-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.archive-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: white;
  border: 1px solid rgba(0,0,0,0.06);
  transition: box-shadow 0.12s, border-color 0.12s, opacity 0.15s;
}

.archive-item:hover {
  box-shadow: 0 1px 6px rgba(0,0,0,0.07);
  border-color: rgba(0,0,0,0.1);
}

.archive-item--loading {
  opacity: 0.5;
  pointer-events: none;
}

.archive-item__check {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
}
.archive-item__check svg { width: 10px; height: 10px; }

.archive-item__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.archive-item__title {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0,0,0,0.6);
  text-decoration: line-through;
  text-decoration-color: rgba(0,0,0,0.25);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.archive-item__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.archive-item__project {
  font-size: 0.72rem;
  color: rgba(0,0,0,0.45);
  font-weight: 500;
}

.archive-item__deadline {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.7rem;
  color: rgba(0,0,0,0.4);
}

.archive-item__archived-date {
  font-size: 0.7rem;
  color: rgba(0,0,0,0.3);
}

.archive-item__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.12s;
}

.archive-item:hover .archive-item__actions { opacity: 1; }

.archive-action {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s;
  background: transparent;
}

.archive-action svg { width: 12px; height: 12px; }

.archive-action--restore {
  color: var(--primary);
  border-color: rgba(61,106,144,0.25);
  background: var(--primary-muted);
}
.archive-action--restore:hover {
  background: rgba(61,106,144,0.12);
  border-color: rgba(61,106,144,0.4);
}

.archive-action--delete {
  color: rgba(0,0,0,0.4);
  border-color: rgba(0,0,0,0.1);
}
.archive-action--delete:hover {
  color: #b03a3a;
  border-color: rgba(190,60,60,0.3);
  background: rgba(190,60,60,0.06);
}

.archive-action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Empty state */
.archive-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 0;
  color: rgba(0,0,0,0.35);
  font-size: 0.875rem;
}

/* Confirm delete */
.archive-confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.archive-confirm {
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 380px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
}

.archive-confirm__text {
  font-size: 0.9rem;
  color: rgba(0,0,0,0.75);
  margin-bottom: 20px;
  line-height: 1.5;
}

.archive-confirm__actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.archive-confirm__btn {
  padding: 0 16px;
  height: 34px;
  border-radius: 7px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.12s;
}

.archive-confirm__btn--cancel {
  background: rgba(0,0,0,0.05);
  color: rgba(0,0,0,0.65);
  border-color: rgba(0,0,0,0.1);
}
.archive-confirm__btn--cancel:hover { background: rgba(0,0,0,0.09); }

.archive-confirm__btn--delete {
  background: #b03a3a;
  color: white;
}
.archive-confirm__btn--delete:hover { background: #922f2f; }
</style>
